import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Typography,
} from '@mui/material';
import { LoginPageStyles } from '../styles/LoginPageStyles';
import { useMutation, useQuery } from '@apollo/client';
import { SEND_PASSWORD_RESET_EMAIL } from '../graphql/sendPasswordResetEmail';
import { VERIFY_PASSWORD_RESET_CODE } from '../graphql/verifyPasswordResetCode';
import { RESET_PASSWORD } from '../graphql/resetPassword';
import { GET_ME } from '../graphql/getMe';
import { Role } from '../__generated__/graphql';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { CommonStyles } from '../styles/CommonStyles';
import BrainPost from '../components/BrainPost';
import { useNavigateHome } from '../hooks/useNavigateHome';

const ResetPassword = () => {
  const [step, setStep] = useState<'email' | 'code' | 'password'>('email');
  const [email, setEmail] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [codeError, setCodeError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [error, setError] = useState<string>('');

  const navigate = useNavigate();
  const { data: me, loading: meLoading } = useQuery(GET_ME);
  const { navigateHome } = useNavigateHome();

  useEffect(() => {
    if (me && !meLoading) {
      navigateHome();
    }
  }, [me, meLoading, navigateHome]);

  const [sendResetEmail, { loading: sendingEmail }] = useMutation(
    SEND_PASSWORD_RESET_EMAIL,
    {
      onCompleted: () => {
        setStep('code');
        setError('');
      },
      onError: (error) => {
        setError(error.message);
      },
    },
  );

  const [verifyCode, { loading: verifyingCode }] = useMutation(
    VERIFY_PASSWORD_RESET_CODE,
    {
      onCompleted: () => {
        setStep('password');
        setError('');
      },
      onError: (error) => {
        setError(error.message);
      },
    },
  );

  const [resetPassword, { loading: resettingPassword }] = useMutation(
    RESET_PASSWORD,
    {
      onCompleted: () => {
        navigate('/login');
      },
      onError: (error) => {
        setError(error.message);
      },
    },
  );

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    return password.length >= 6;
  };

  const handleSendResetEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!email) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    try {
      await sendResetEmail({ variables: { email } });
    } catch (e) {
      console.error('Error sending reset email:', e);
    }
  };

  const handleVerifyCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!code) {
      setCodeError('Code is required');
      return;
    }
    setCodeError('');
    try {
      await verifyCode({ variables: { email, code } });
    } catch (e) {
      console.error('Error verifying code:', e);
    }
  };

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!newPassword) {
      setPasswordError('Password is required');
      return;
    }
    if (!validatePassword(newPassword)) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }
    setPasswordError('');
    try {
      await resetPassword({ variables: { email, code, newPassword } });
    } catch (e) {
      console.error('Error resetting password:', e);
    }
  };

  if (meLoading) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <CircularProgress size={70} />
      </Box>
    );
  }

  return (
    <Box sx={LoginPageStyles.mainContainer}>
      <BrainPost />
      <Box sx={LoginPageStyles.rightContainer}>
        <Box
          sx={LoginPageStyles.loginContainer}
          component="form"
          onSubmit={
            step === 'email'
              ? handleSendResetEmail
              : step === 'code'
              ? handleVerifyCode
              : handleResetPassword
          }
        >
          <Typography sx={LoginPageStyles.title}>Reset Password</Typography>

          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {step === 'email' && (
            <>
              <TextField
                sx={LoginPageStyles.textField}
                label="Email"
                variant="outlined"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={!!emailError}
                helperText={emailError}
              />
              <Button
                sx={LoginPageStyles.button}
                type="submit"
                variant="contained"
                loading={sendingEmail}
              >
                Send Reset Code
              </Button>
            </>
          )}

          {step === 'code' && (
            <>
              <TextField
                sx={LoginPageStyles.textField}
                label="Reset Code"
                variant="outlined"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                error={!!codeError}
                helperText={codeError}
              />
              <Button
                sx={LoginPageStyles.button}
                type="submit"
                variant="contained"
                loading={verifyingCode}
              >
                Verify Code
              </Button>
            </>
          )}

          {step === 'password' && (
            <>
              <FormControl
                sx={{ ...LoginPageStyles.textField, minWidth: '240px' }}
              >
                <InputLabel htmlFor="outlined-adornment-password">
                  New Password
                </InputLabel>
                <OutlinedInput
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  error={!!passwordError}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  onChange={(event) => setNewPassword(event.target.value)}
                />
                {passwordError && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {passwordError}
                  </Typography>
                )}
              </FormControl>
              <Button
                sx={LoginPageStyles.button}
                type="submit"
                variant="contained"
                loading={resettingPassword}
              >
                Reset Password
              </Button>
            </>
          )}
        </Box>
        <Button
          sx={LoginPageStyles.signupButton}
          variant="outlined"
          onClick={() => navigate('/login')}
        >
          Back to Login
        </Button>
      </Box>
    </Box>
  );
};

export default ResetPassword;
